import { useState } from 'react'
import { trpc } from '../../lib/trpc'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card'
import { Badge } from '../ui/badge'
import { Mail, Copy, X, UserPlus, Users, Clock, CheckCircle, AlertCircle } from 'lucide-react'
import { toast } from 'sonner'
import { useAppStore } from '../../stores/app-store'

export default function InvitationsTab() {
  const [email, setEmail] = useState('')
  const [role, setRole] = useState('MEMBER')
  const [isInviting, setIsInviting] = useState(false)
  const activeWorkspace = useAppStore((state) => state.activeWorkspace)
  
  const { data: invitations = [], refetch: refetchInvitations } = trpc.auth.listInvitations.useQuery(
    { workspaceId: activeWorkspace?.id || '' },
    { enabled: !!activeWorkspace?.id }
  )
  
  const createInvitation = trpc.auth.createInvitation.useMutation({
    onSuccess: () => {
      toast.success(`An invitation has been sent to ${email}`)
      setEmail('')
      setIsInviting(false)
      refetchInvitations()
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to send invitation')
      setIsInviting(false)
    },
  })
  
  const cancelInvitation = trpc.auth.cancelInvitation.useMutation({
    onSuccess: () => {
      toast.success('The invitation has been cancelled')
      refetchInvitations()
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to cancel invitation')
    },
  })
  
  const handleInvite = () => {
    if (!email || !activeWorkspace?.id) return
    
    setIsInviting(true)
    createInvitation.mutate({
      email,
      workspaceId: activeWorkspace.id,
      role: role as 'ADMIN' | 'MEMBER' | 'VIEWER',
    })
  }
  
  const copyInviteLink = (token: string) => {
    const inviteUrl = `${window.location.origin}/invite/${token}`
    navigator.clipboard.writeText(inviteUrl)
    toast.success('Invitation link has been copied to clipboard')
  }
  
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'PENDING':
        return <Badge variant="secondary" className="gap-1"><Clock className="w-3 h-3" /> Pending</Badge>
      case 'ACCEPTED':
        return <Badge variant="default" className="gap-1 bg-green-600"><CheckCircle className="w-3 h-3" /> Accepted</Badge>
      case 'EXPIRED':
        return <Badge variant="destructive" className="gap-1"><AlertCircle className="w-3 h-3" /> Expired</Badge>
      case 'CANCELLED':
        return <Badge variant="outline" className="gap-1"><X className="w-3 h-3" /> Cancelled</Badge>
      default:
        return <Badge variant="outline">{status}</Badge>
    }
  }
  
  const getRoleBadge = (role: string) => {
    switch (role) {
      case 'ADMIN':
        return <Badge variant="default" className="bg-krushr-primary">Admin</Badge>
      case 'MEMBER':
        return <Badge variant="secondary">Member</Badge>
      case 'VIEWER':
        return <Badge variant="outline">Viewer</Badge>
      default:
        return <Badge variant="outline">{role}</Badge>
    }
  }
  
  if (!activeWorkspace) {
    return (
      <div className="p-8 text-center text-muted-foreground">
        No active workspace selected
      </div>
    )
  }
  
  return (
    <div className="space-y-6">
      {/* Send Invitation Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <UserPlus className="w-5 h-5" />
            Invite Team Members
          </CardTitle>
          <CardDescription>
            Invite new members to join your workspace
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4">
            <div className="flex-1">
              <Label htmlFor="email">Email address</Label>
              <Input
                id="email"
                type="email"
                placeholder="colleague@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleInvite()}
              />
            </div>
            <div className="w-32">
              <Label htmlFor="role">Role</Label>
              <Select value={role} onValueChange={setRole}>
                <SelectTrigger id="role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ADMIN">Admin</SelectItem>
                  <SelectItem value="MEMBER">Member</SelectItem>
                  <SelectItem value="VIEWER">Viewer</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <Button 
                onClick={handleInvite}
                disabled={!email || isInviting}
              >
                {isInviting ? 'Sending...' : 'Send Invite'}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Invitations List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5" />
            Pending Invitations
          </CardTitle>
          <CardDescription>
            Manage invitations sent to team members
          </CardDescription>
        </CardHeader>
        <CardContent>
          {invitations.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Mail className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No pending invitations</p>
              <p className="text-sm mt-1">Invite team members to start collaborating</p>
            </div>
          ) : (
            <div className="space-y-3">
              {invitations.map((invitation) => (
                <div
                  key={invitation.id}
                  className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 transition-colors"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <Mail className="w-4 h-4 text-muted-foreground" />
                      <span className="font-medium">{invitation.email}</span>
                      {getRoleBadge(invitation.role)}
                      {getStatusBadge(invitation.status)}
                    </div>
                    <div className="text-sm text-muted-foreground mt-1">
                      Invited by {invitation.invitedBy.name} on{' '}
                      {new Date(invitation.createdAt).toLocaleDateString()}
                      {invitation.status === 'PENDING' && (
                        <span className="ml-2">
                          • Expires on {new Date(invitation.expiresAt).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  </div>
                  {invitation.status === 'PENDING' && (
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => copyInviteLink(invitation.token)}
                        title="Copy invitation link"
                      >
                        <Copy className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => cancelInvitation.mutate({ invitationId: invitation.id })}
                        title="Cancel invitation"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}